import React from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {selectTests} from '../../slices/testSlice';
import {useDashboardData} from '../../hooks/useDashboardData';
import {categorizeBiomarkerStatuses} from '../../utils/biomarkerUtils';
import {BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid} from 'recharts';
import {useAuth} from '../../hooks/useAuth';

// Custom tick component for X-axis that splits labels into two lines
const CustomXAxisTick = React.memo(function CustomXAxisTick(props: any) {
    const {x, y, payload} = props;
    const words = payload.value.split(' ');

    return (
        <text
            x={x}
            y={y}
            textAnchor="middle"
            fill="#013B4B"
            style={{
                fontFamily: 'Poppins',
                fontSize: '14px',
                fontWeight: 500,
            }}>
            {words.map((word: string, index: number) => (
                <tspan key={index} x={x} dy={index === 0 ? 0 : 16}>
                    {word}
                </tspan>
            ))}
        </text>
    );
});

// Custom label component that matches text color to bar color and hides when value is 0
const CustomLabel = React.memo(function CustomLabel(props: any) {
    const {x, y, width, value, index} = props;

    // Hide label if value is 0
    if (value === 0) {
        return null;
    }

    // Define colors array matching the data order: Normal, Borderline, Critical
    const colors = ['#42D4B0', '#FFBE7A', '#F15E87'];
    const color = colors[index] || '#42D4B0';

    return (
        <text
            x={x + width / 2}
            y={y - 10}
            fill={color}
            textAnchor="middle"
            style={{
                fontFamily: 'Poppins',
                fontSize: '26px',
                fontWeight: 400,
                lineHeight: 'auto',
                letterSpacing: '0%',
            }}
            key={`markers-label-${index}-${value}`} // Stable key for labels
        >
            {value}
        </text>
    );
});

const MarkersChart: React.FC = () => {
    const testsState = useSelector(selectTests);
    const navigate = useNavigate();
    const {user} = useAuth();

    // Get gender from user info for gender-specific biomarker configs
    const gender: 'male' | 'female' | undefined =
        user?.userInfo?.gender === 'F' ? 'female' : user?.userInfo?.gender === 'M' ? 'male' : undefined;

    // Use dashboard-specific data (dummy data if not subscribed)
    const {data: tests} = useDashboardData();
    const isLoading = testsState.loading;

    // Get biomarker status counts for all tests (not filtered by category) - with gender for gender-specific biomarkers
    const statusCounts = React.useMemo(() => {
        return categorizeBiomarkerStatuses(tests, gender);
    }, [tests, gender]);

    // Convert to chart data format - only recalculate when status counts change
    const chartData = React.useMemo(
        () => [
            {name: 'Within Range', value: statusCounts.normal, fill: '#42D4B0'},
            {name: 'Needs Attention', value: statusCounts.borderline, fill: '#FFBE7A'},
            {name: 'High Priority', value: statusCounts.critical, fill: '#F15E87'},
        ],
        [statusCounts]
    );

    // Handle bar click - navigate to biomarkers page with status filter
    const handleBarClick = (data: any) => {
        if (!data || !data.name) return;
        // Map chart labels to URL params
        const statusMap: Record<string, string> = {
            'Within Range': 'within-range',
            'Needs Attention': 'needs-attention',
            'High Priority': 'high-priority',
        };
        const statusParam = statusMap[data.name] || 'all';
        navigate(`/results/biomarkers?status=${statusParam}`);
    };

    // Show loading state
    if (isLoading) {
        return (
            <div className="relative">
                <div className="text-center py-20 text-mld-card-text">Loading markers overview...</div>
            </div>
        );
    }

    // Show empty state if no data
    const totalMarkers = statusCounts.normal + statusCounts.borderline + statusCounts.critical;
    if (totalMarkers === 0) {
        return (
            <div className="relative">
                <div className="text-center py-20 text-mld-card-text">No markers data available</div>
            </div>
        );
    }

    return (
        <div className="relative">
            <ResponsiveContainer width="100%" height={350}>
                <BarChart
                    data={chartData}
                    margin={{top: 40, right: 10, left: 10, bottom: 35}}
                    maxBarSize={65}
                    key="markers-chart" // Stable key to prevent unnecessary re-mounts
                >
                    {/* Cartesian grid for background lines */}
                    <CartesianGrid
                        strokeDasharray="0"
                        stroke="#E9E9E4"
                        strokeWidth={1}
                        horizontal={true}
                        vertical={false}
                    />

                    <XAxis
                        dataKey="name"
                        tick={<CustomXAxisTick />}
                        axisLine={{stroke: '#013B4B', strokeWidth: 1}}
                        tickLine={false}
                        tickMargin={12}
                        interval={0}
                    />

                    <YAxis hide />

                    <Tooltip
                        contentStyle={{
                            backgroundColor: '#fff',
                            border: '1px solid #E0E0E0',
                            borderRadius: '0.5rem',
                            fontSize: '12px',
                        }}
                    />
                    <Bar
                        dataKey="value"
                        radius={[32.5, 32.5, 0, 0]}
                        label={<CustomLabel />}
                        animationDuration={200}
                        isAnimationActive={false} // Disable default animations to prevent flickering
                        onClick={handleBarClick}
                        cursor="pointer"
                    />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default MarkersChart;
